import { useEffect } from 'react';
import { useData } from './useData';

/**
 * Dynamically updates PWA-related meta tags based on settings from the API.
 * The static manifest.json in /public provides the base PWA config (icons, display, scope).
 * This hook overrides the app name and theme color at runtime using meta tags.
 */
export const useDynamicManifest = () => {
    const { allSettings } = useData();

    useEffect(() => {
        if (!allSettings?.data) return;

        const data = allSettings.data;
        const name = data.marketplaceName || '';
        const themeColor = data.layouts?.find((item) => item.name === 'primary color')?.value || '';
        const faviconSrc = data.layouts?.find((item) => item.name === 'favicon')?.value || '';

        // Update application name (used by browsers for installed PWA name)
        setOrCreateMeta('application-name', name);
        // iOS home screen name
        setOrCreateMeta('apple-mobile-web-app-title', name);
        // Update page title as fallback
        if (name) {
            document.title = name;
        }

        // Update theme color
        if (themeColor) {
            setOrCreateMeta('theme-color', themeColor);
        }

        // Add apple-touch-icon for iOS PWA icon (uses API favicon)
        if (faviconSrc) {
            updateAppleTouchIcon(faviconSrc);
        }
    }, [allSettings]);
};

function setOrCreateMeta(name: string, content: string) {
    if (!content) return;

    let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
    if (meta) {
        meta.content = content;
    } else {
        meta = document.createElement('meta');
        meta.name = name;
        meta.content = content;
        document.head.appendChild(meta);
    }
}

function updateAppleTouchIcon(href: string) {
    let link = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement | null;
    if (link) {
        link.href = href;
    } else {
        link = document.createElement('link');
        link.rel = 'apple-touch-icon';
        link.href = href;
        document.head.appendChild(link);
    }
}
